import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { fetchPennylaneSettings, pennylaneQueryKeys } from '@/features/pennylane/api.js';
import { ApiError } from '@/lib/api/client.js';
import { CatchupSection } from './catchup-section.js';
import { PennylaneSettingsSection } from './pennylane-settings-section.js';
import { SettingsSectionSkeleton } from './settings-parts.js';

/**
 * Onglet **Pennylane** (`02` §12.4), refonte du 10/09/2026 : les réglages
 * opérationnels (activation, mode, planification, fenêtre de rattrapage,
 * transporteur des factures) et le rattrapage par date vivent au même endroit.
 *
 * Une seule API : `/api/pennylane/settings`. L'ancien couple
 * `GET/PUT /api/settings/pennylane` — qui ne portait que l'URL et la clé —
 * a disparu : deux routes pour un même objet, c'était deux vérités possibles.
 */
export function PennylaneTab(): JSX.Element {
  const query = useQuery({
    queryKey: pennylaneQueryKeys.settings(),
    queryFn: fetchPennylaneSettings,
  });

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <SettingsSectionSkeleton fields={6} />
        <SettingsSectionSkeleton fields={2} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <PageSection title="Planification et réglages">
        <ErrorState
          message={
            query.error instanceof ApiError
              ? query.error.message
              : 'Impossible de charger les réglages Pennylane.'
          }
          detail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => {
            void query.refetch();
          }}
          className="border-0 bg-transparent py-6"
        />
      </PageSection>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PennylaneSettingsSection settings={query.data} />
      <CatchupSection source="pennylane" />
    </div>
  );
}
